import NamePlateComponent from "@/components/dashboard/general/svgs/NamePlateComponent";
import WelcomeModal from "@/components/dashboard/WelcomeModal";
import { MARK_WELCOME_MESSAGE_SEEN_MUTATION } from "@/graphql/mutations/playerMutations";
import { LIVE_STREAMS_SUBSCRIPTION } from "@/graphql/subscriptions/streamTeamSubscriptions";
import { useAuth } from "@/hooks/auth/useAuth";
import { convertLetters, convertLettersSafely } from "@/utils/letterConversion";
import { useMutation, useSubscription } from "@apollo/client";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface TileProps {
    title: string;
    image: string;
    link?: string;
    disabled?: boolean;
    showLiveIndicator?: boolean;
}

const DashboardTile: React.FC<TileProps> = ({
    title,
    image,
    link,
    disabled = false,
    showLiveIndicator = false,
}) => {
    const content = (
        <div className="relative h-full w-full max-w-[320px]">
            {link && !disabled ? (
                <Link to={link}>
                    <img
                        src={image}
                        alt={title}
                        className="border-brand h-80 w-80 rounded-lg border-8 object-cover"
                    />
                </Link>
            ) : (
                <>
                    <img
                        src={image}
                        alt={title}
                        className="border-brand h-80 w-80 rounded-lg border-8 object-cover opacity-90 grayscale"
                    />
                    <div className="absolute inset-0 rounded-lg bg-gray-500 opacity-30" />
                </>
            )}
            <NamePlateComponent
                text={title}
                className="absolute -bottom-6 left-1/2 z-10 -translate-x-[calc(50%+4px)] transform"
                containerWidth={300}
                overrideFontSize
                defaultFontSize={26}
                style={disabled ? { filter: "grayscale(100%)" } : {}}
            />
            {showLiveIndicator && (
                <div className="absolute top-4 right-4 z-20">
                    <span className="relative flex h-4 w-4">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-80" />
                        <span className="relative inline-flex h-4 w-4 rounded-full border border-red-200 bg-red-600" />
                    </span>
                </div>
            )}
        </div>
    );

    return (
        <div className="flex items-center justify-center p-2">{content}</div>
    );
};

// Holds the live streams subscription itself, so updates only re-render this tile.
const RavenguardTile: React.FC = () => {
    const { data } = useSubscription<{
        liveStreams: { id: string; isLive: boolean }[];
    }>(LIVE_STREAMS_SUBSCRIPTION);

    return (
        <DashboardTile
            title="RAVENGUARD"
            image="/rookery-tile.png"
            link="https://ravenguard.vanderheim.com"
            showLiveIndicator={
                data?.liveStreams?.some((stream) => stream.isLive) ?? false
            }
        />
    );
};

export default function DashboardPage() {
    const { user, fetchUserData } = useAuth();
    const [markWelcomeMessageSeen] = useMutation(
        MARK_WELCOME_MESSAGE_SEEN_MUTATION,
    );

    // Show welcome modal if user hasn't seen it yet
    const player = user?.player;
    const shouldShowWelcomeModal =
        player?.hasSeenWelcomeMessage === false && !!player?.shield;
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);

    // Sync modal visibility with player data (handles async data loading)
    useEffect(() => {
        if (shouldShowWelcomeModal) {
            setShowWelcomeModal(true);
        }
    }, [shouldShowWelcomeModal]);

    const handleCloseWelcomeModal = async () => {
        setShowWelcomeModal(false);
        try {
            await markWelcomeMessageSeen();
        } catch (error) {
            console.error("[WelcomeModal] mutation error:", error);
        }
        // Refresh user data to update the hasSeenWelcomeMessage flag
        await fetchUserData();
    };

    const title = convertLetters("Welcome to <br/> Vanderheim");
    const twitchUsername = user
        ? convertLettersSafely(user?.twitchUsername)
        : "";
    const shield = user?.player?.shield || null;

    return (
        <div className="w-full px-4 py-8">
            <div className="text-center">
                <h1
                    className="font-prviking border-text text-brand text-5xl drop-shadow-lg sm:text-7xl lg:text-9xl"
                    dangerouslySetInnerHTML={{ __html: title }}
                />
                {twitchUsername && (
                    <h2 className="font-prviking border-text text-brand mt-4 text-4xl drop-shadow-lg sm:text-5xl">
                        {twitchUsername}
                    </h2>
                )}
            </div>

            <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                <DashboardTile
                    title="PROFILE"
                    image={(shield?.imageWithTagsUrl || shield?.imageUrl)!}
                    link="profile"
                />
                <DashboardTile
                    title="WALLHALLA"
                    image="/wallhalla.png"
                    link="https://wallhalla.vanderheim.com"
                />
                <DashboardTile
                    title="LEADERBOARDS"
                    image="/leaderboards.png"
                    link="leaderboards"
                />
                <DashboardTile
                    title="CLOTHING"
                    image="/clothing.png"
                    link="https://clothing.vanderheim.com"
                />
            </div>
            <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-3 lg:grid-cols-3">
                <RavenguardTile />
                <DashboardTile
                    title="FREEPLAY FORGE"
                    image="/freeplay-forge.png"
                    link="/freeplay-forge"
                />
                <DashboardTile
                    title="MARKETPLACE"
                    image="/viking-marketplace.webp"
                    disabled
                />
            </div>

            {/* Welcome Modal for new shield creators */}
            <WelcomeModal
                isOpen={showWelcomeModal ?? false}
                closeModal={handleCloseWelcomeModal}
                username={user?.twitchUsername || "Viking"}
                clanName={player?.clan?.name || "your clan"}
            />
        </div>
    );
}
